import { Card } from '#/components/ui/card';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import Icon from '../Icon';
import { validateResolution } from './util';
import { Dialog, DialogContent, DialogTitle } from '#/components/ui/dialog';
import Cropper from 'react-easy-crop';
import { Button } from '#components/ui/button.tsx';
import './range.css';

export interface CropArea {
  width: number;
  height: number;
  x: number;
  y: number;
}

export const getCroppedImg = async (imageSrc: string, crop: CropArea): Promise<string> => {
  const image = new Image();
  image.src = imageSrc;

  await new Promise((resolve) => {
    image.onload = resolve;
  });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Não foi possível obter o contexto 2D do canvas');
  }

  canvas.width = crop.width;
  canvas.height = crop.height;

  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Não foi possível processar a imagem'));
        return;
      }
      const url = URL.createObjectURL(blob);
      resolve(url);
    }, 'image/jpeg');
  });
};

interface DropzoneProps extends React.HTMLAttributes<HTMLDivElement> {
  onFilesSelected?: (files: File[]) => void;
  variant?: 'default' | 'square' | 'thumb' | 'video';
  title?: string;
  description?: string;
  showIcon?: boolean;
  icon?: ReactElement;
  requiredWidth?: number;
  requiredHeight?: number;
  requiredAspectRatio?: string;
  removable?: boolean;
  value?: File[];
  areaWidth?: string;
  readonly?: boolean;
  cropModal?: boolean;
  cropValue?: CropArea;
  onCropChange?: (crop: CropArea) => void;
  classNameContent?: string;
  mimeTypes?: string[];
  maxSizeInBytes?: number;
}

const Dropzone = React.forwardRef<HTMLDivElement, DropzoneProps>(
  (
    {
      onFilesSelected,
      variant = 'default',
      title,
      description,
      showIcon = true,
      icon = <Icon size={48} className="h-12 w-12 text-[var(--gray-secondary)]" name="upload" />,
      requiredWidth,
      requiredHeight,
      requiredAspectRatio,
      value = [],
      areaWidth = '176',
      className,
      readonly = false,
      cropModal = false,
      cropValue,
      onCropChange,
      classNameContent = '',
      mimeTypes = ['image/jpeg', 'image/png'],
      maxSizeInBytes = 5 * 1024 * 1024,
      ...rest
    },
    ref
  ) => {
    const [files, setFiles] = useState<File[]>([]);
    const [displayImage, setDisplayImage] = useState<string | null>(null);

    const [open, setOpen] = useState(false);
    const [cropModalFile, setCropModalFile] = useState<File | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const formatedAspectRatio = () => {
      if (requiredAspectRatio) {
        const [width, height] = requiredAspectRatio.split(':').map(Number);
        return width / height;
      }
      return 9 / 16;
    };

    const onCropComplete = (croppedArea: CropArea, croppedAreaPixels: CropArea) => {
      onCropChange?.(croppedAreaPixels);
    };

    useEffect(() => {
      if (value.length) {
        setFiles(value);
      }
    }, [value]);

    const validateMidia = ({ file }: { file: File }) => {
      const showError = (type: 'image' | 'video', res: ReturnType<typeof validateResolution>) => {
        if (!res.matchesWidth || !res.matchesHeight) {
          toast.error(`${type === 'image' ? 'Imagem' : 'Vídeo'} deve ter resolução ${requiredWidth}x${requiredHeight}`);
        } else if (!res.matchesRatio) {
          toast.error(`${type === 'image' ? 'Imagem' : 'Vídeo'} deve estar na proporção ${requiredAspectRatio}`);
        }
      };

      if (file.type.startsWith('image')) {
        const img = new Image();
        img.onload = () => {
          const imgWidth = cropModal && cropValue ? cropValue.width : img.naturalWidth;
          const imgHeight = cropModal && cropValue ? cropValue.height : img.naturalHeight;

          const result = validateResolution(imgWidth, imgHeight, requiredWidth, requiredHeight, requiredAspectRatio);
          if (!result.isValid) {
            showError('image', result);
            return;
          }

          setFiles([file]);
          onFilesSelected?.([file]);
        };
        img.onerror = () => toast.error('Erro ao carregar a imagem.');
        img.src = URL.createObjectURL(file);
      } else if (file.type.startsWith('video')) {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          const result = validateResolution(video.videoWidth, video.videoHeight);
          if (!result.isValid) {
            showError('video', result);
            return;
          }

          setFiles([file]);
          onFilesSelected?.([file]);
        };
        video.onerror = () => toast.error('Erro ao carregar o vídeo.');
        video.src = URL.createObjectURL(file);
      } else {
        toast.error('Tipo de arquivo não suportado.');
      }
    };

    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        if (cropModal) {
          setOpen(true);
          setCropModalFile(file);
          return;
        }

        validateMidia({ file });
      },
      [onFilesSelected, requiredWidth, requiredHeight, requiredAspectRatio]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: mimeTypes.reduce<Record<string, string[]>>((acc, type) => {
        acc[type] = [];
        return acc;
      }, {}),
      multiple: false,
      disabled: readonly,
      maxSize: maxSizeInBytes,
      onDropRejected: (fileRejections) => {
        const errorCode = fileRejections[0]?.errors[0].code || 'unknown_error';
        if (errorCode === 'file-too-large') {
          toast.error(`O arquivo excede o tamanho máximo permitido de ${maxSizeInBytes / (1024 * 1024)}MB.`);
        } else if (errorCode === 'file-invalid-type') {
          toast.error('Formato de arquivo não suportado. Use os formatos permitidos.');
        } else {
          toast.error('Erro ao processar o arquivo. Tente novamente.');
        }
      },
    });

    const closeModal = () => {
      setOpen(false);
    };

    // const handleEditImageButtonClick = () => {
    //   if (readonly) {
    //     return;
    //   }
    //   if (removable) {
    //     setFiles([]);
    //   }
    //   open();
    // };

    return (
      <div className={`flex flex-col items-center gap-4 ${className ?? ''}`} {...rest} data-testid={title}>
        <Card
          {...getRootProps()}
          className={`hover:border-primary grow-0 items-center justify-center overflow-hidden rounded-[5px] border-1 border-dashed border-[var(--gray-primary)] p-4 text-center ${
            variant === 'square'
              ? `aspect-square w-[${areaWidth}px] h-[${areaWidth}px]`
              : variant === 'thumb'
                ? 'aspect-[9/16]'
                : variant === 'video'
                  ? 'aspect-video h-full w-full'
                  : 'h-60 w-full'
          } ${!readonly ? 'cursor-pointer' : ''} ${classNameContent}`}
          style={{
            width: variant === 'square' ? `${areaWidth}px` : '',
            // backgroundColor: 'var(--primary)' + '10',
          }}
          ref={ref}
        >
          <input {...getInputProps()} tabIndex={readonly ? -1 : 0} />
          {files.length === 0 ? (
            <div className="flex w-full flex-col items-center justify-center gap-2 px-2 text-center">
              {showIcon && icon}
              {title && (
                <span
                  className="font-[Figtree] text-base text-[14px] leading-[14px] font-semibold break-words text-[var(--gray-secondary)]"
                  style={{ lineHeight: '14px', fontSize: '14px', letterSpacing: 0, font: 'Figtree' }}
                >
                  {isDragActive ? 'Solte os arquivos aqui...' : title}
                </span>
              )}
              {description && (
                <span
                  className="font-[Figtree] text-[12px] break-words text-[var(--gray-secondary)]"
                  style={{ lineHeight: '14px', fontSize: '12px', fontWeight: 400, letterSpacing: 0, font: 'Figtree' }}
                >
                  {description}
                </span>
              )}
            </div>
          ) : (
            <div className="grid w-full grid-cols-1">
              {files.map((file) => {
                return (
                  <div key={file.name} className="relative col-span-full">
                    {file.type.startsWith('image') ? (
                      <img
                        src={displayImage || URL.createObjectURL(file)}
                        alt={file.name}
                        className="h-full w-full rounded object-cover"
                      />
                    ) : file.type.startsWith('video') ? (
                      <video src={URL.createObjectURL(file)} controls className="h-full w-full rounded object-cover" />
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Crop Modal */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTitle>Editar imagem</DialogTitle>
          <DialogContent aria-describedby="Editar a imagem selecionada">
            <div className="flex min-h-[400px] flex-col gap-6">
              <h1 className="text-primary text-xl leading-5 font-semibold md:text-2xl">Editar Imagem</h1>
              <div className="relative h-full flex-1">
                {cropModalFile && (
                  <Cropper
                    image={URL.createObjectURL(cropModalFile)}
                    crop={crop}
                    zoom={zoom}
                    aspect={formatedAspectRatio()}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                )}
              </div>
              <div className="mx-auto flex h-10 w-[70%] items-center gap-1">
                <Icon name="zoom_out" size={24} />
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.01}
                  aria-labelledby="Zoom"
                  onChange={(e) => {
                    setZoom(parseFloat(e.target.value));
                  }}
                  className="range-zoom"
                />
                <Icon name="zoom_in" size={24} />
              </div>
              <div className="flex justify-between">
                <Button
                  type="button"
                  variant="outline"
                  className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-fit sm:grow-0"
                  onClick={closeModal}
                >
                  <span className="[font-family:'Figtree'] text-sm">Cancelar</span>
                </Button>
                <Button
                  type="button"
                  className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-fit sm:grow-0"
                  onClick={async () => {
                    setOpen(false);
                    if (!cropModalFile || !cropValue) {
                      toast.error('Erro ao processar a imagem. Tente novamente.');
                      return;
                    }
                    try {
                      const result = await getCroppedImg(URL.createObjectURL(cropModalFile), cropValue);
                      setDisplayImage(result);
                      validateMidia({ file: cropModalFile });
                    } catch (_error) {
                      toast.error('Erro ao processar a imagem. Tente novamente.');
                    }
                  }}
                >
                  <span className="[font-family:'Figtree'] text-sm">Concluir</span>
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* {!readonly && (
          <div className="flex grow-0 justify-center">
            <Button
              type="button"
              variant={'outline'}
              className={`text-primary hover:text-primary flex cursor-pointer items-center gap-2.5 rounded-[5px] border-2 border-[var(--primary)] px-5 py-2.5 font-bold disabled:border-[var(--gray-primary)] disabled:text-[var(--gray-primary)]`}
              onClick={handleEditImageButtonClick}
              disabled={files.length === 0}
            >
              <Icon
                name="image"
                color={files.length === 0 ? 'var(--gray-primary)' : 'var(--primary)'}
                fill={1}
                variant={'outlined'}
              />
              <span className="[font-family:'Figtree'] text-sm">Editar imagem</span>
            </Button>
          </div>
        )} */}
      </div>
    );
  }
);

export default Dropzone;
